'use client';

import { useEffect, useRef } from 'react';

export function SerwistUpdatePrompt() {
  // Only enable in production to avoid dev rebuilds constantly triggering updates
  const enabled =
    typeof window !== 'undefined' &&
    process.env.NODE_ENV === 'production' &&
    'serviceWorker' in navigator;

  // Note: typed as unknown then narrowed after dynamic import, keeps bundle down
  const serwistRef = useRef<unknown>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const reloadOnReconnect = () => {
      window.location.reload();
    };
    window.addEventListener('online', reloadOnReconnect);

    let mounted = true;
    let cleanup: (() => void) | undefined;

    const setup = async () => {
      const mod = await import('@serwist/window');
      if (!mounted) return;
      const SerwistWindow = mod.Serwist;
      const serwist = new SerwistWindow('/firebase-messaging-sw.js', {
        scope: '/',
      });
      serwistRef.current = serwist;

      // 1) Proactively unregister legacy SWs (e.g. old '/sw.js') so the new SW can take precedence
      const unregisterLegacySWs = async () => {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            regs.map(async (r) => {
              const urls = [
                r.active?.scriptURL,
                r.waiting?.scriptURL,
                r.installing?.scriptURL,
              ].filter(Boolean) as string[];
              // If any worker in this registration was the legacy '/sw.js', unregister it.
              const hasLegacy = urls.some((u) => {
                try {
                  const p = new URL(u).pathname;
                  return p.endsWith('/sw.js');
                } catch {
                  return false;
                }
              });
              if (hasLegacy) {
                try {
                  await r.unregister();
                } catch {
                  // ignore
                }
              }
            }),
          );
        } catch {
          // ignore
        }
      };
      await unregisterLegacySWs();

      // Track the current registration so we can inspect the waiting worker.
      let reg: ServiceWorkerRegistration | undefined;

      // Activate the newly installed SW when it reaches 'waiting'
      const onWaiting = () => {
        // Auto-activate the new SW immediately if possible
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (serwist as any).messageSkipWaiting?.();
        } catch {
          serwist.messageSkipWaiting?.();
        }
      };
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (serwist as any).addEventListener('waiting', onWaiting, { once: true });
      } catch {
        serwist.addEventListener('waiting', onWaiting);
      }
      // Do not auto-reload on activation; allow silent takeover

      reg = await serwist.register();

      // If a SW is already waiting (e.g. Android updated in background), activate it immediately
      try {
        const existing =
          reg || (await navigator.serviceWorker.getRegistration('/'));
        if (existing?.waiting && existing.waiting.state === 'installed') {
          reg = existing;
          // Attempt immediate activation
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (serwist as any).messageSkipWaiting?.();
          } catch {
            serwist.messageSkipWaiting?.();
          }
        }
      } catch {
        // ignore
      }

      // While app is in foreground: activate as soon as install completes
      const onUpdateFound = () => {
        const installing = reg?.installing;
        if (!installing) return;
        const onStateChange = () => {
          if (reg?.waiting && reg.waiting.state === 'installed') {
            // Try to activate immediately
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (serwist as any).messageSkipWaiting?.();
            } catch {
              serwist.messageSkipWaiting?.();
            }
            try {
              installing.removeEventListener('statechange', onStateChange);
            } catch {
              // ignore
            }
          }
        };
        installing.addEventListener('statechange', onStateChange);
      };
      try {
        reg?.addEventListener('updatefound', onUpdateFound);
      } catch {
        // ignore
      }

      // Nudge browsers to check for updates when tab/app returns to foreground
      const onVisibilityOrFocus = async () => {
        if (document.visibilityState !== 'visible') return;
        try {
          const currentReg =
            reg || (await navigator.serviceWorker.getRegistration('/'));
          if (currentReg) {
            try {
              await currentReg.update();
            } catch {
              // ignore
            }
          }
        } catch {
          // ignore
        }
      };
      document.addEventListener('visibilitychange', onVisibilityOrFocus);
      window.addEventListener('focus', onVisibilityOrFocus);

      // Basic cleanup: remove 'waiting' listener on unmount
      cleanup = () => {
        serwist.removeEventListener('waiting', onWaiting);
        try {
          reg?.removeEventListener('updatefound', onUpdateFound);
        } catch {
          // ignore
        }
        try {
          document.removeEventListener('visibilitychange', onVisibilityOrFocus);
          window.removeEventListener('focus', onVisibilityOrFocus);
        } catch {
          // ignore
        }
      };
    };

    void setup();

    return () => {
      mounted = false;
      window.removeEventListener('online', reloadOnReconnect);
      cleanup?.();
    };
  }, [enabled]);

  return null;
}
